import sentry from '../../../sentry';
import {
  Button,
  Box,
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  FormControl,
  VStack,
  Link,
  Text,
  Checkbox,
  Divider,
  Collapse,
  useColorModeValue,
  Flex,
  Input,
} from '@chakra-ui/react';
import { Controller } from 'react-hook-form';
import { useDisclosure } from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { useCallback, useContext, useEffect, useState } from 'react';
import LiveEventDataListContext from '../../../store/client/LiveEventDataListContext';
import {
  CustomReactSelect,
  formatOptionLabelSelectPassType,
  formatOptionLabelFilterGroupTag,
  formatOptionLabelFilterPassTemplate,
} from '../../common/CustomReactSelect';
import { HttpContext } from '../../../context/HttpContext';
import { MeContext } from '../../../context/MeContext';
import CustomSearchableSelect from '../../common/CustomSearchableSelect';
import { fetchGroupTags } from '../../../services/groupTagService';
import CustomDatePicker from '../../common/CustomDatePicker/CustomDatePicker';
import { endOfDayISO, startOfDayISO } from './scanDateBounds';
import FilterButton from '../../common/FilterButton';
import ClearFiltersButton from '../../common/ClearFiltersButton';
import { useReportAndToastApiError } from '../../../hooks/useReportAndToastApiError';

const emptyFilterValues = () => ({
  reader: '',
  groupTags: [],
  pass: '',
  scanType: '',
  status: '',
  validationType: '',
  passTemplate: [],
  createdAfter: '',
  createdBefore: '',
});

function LiveEventDataListFilter() {
  const reportAndToastApiError = useReportAndToastApiError();
  const { authAxios } = useContext(HttpContext);
  const [totalActiveFilters, setTotalActiveFilters] = useState(0);
  const [readers, setReaders] = useState([]);
  const [groupTagsList, setGroupTagsList] = useState([]);
  const [passTemplatesList, setPassTemplatesList] = useState([]);
  const [dateRangeKey, setDateRangeKey] = useState(0);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { register, handleSubmit, reset, watch, control, setValue } = useForm();
  const liveEventDataListCtx = useContext(LiveEventDataListContext);
  const meCtx = useContext(MeContext);
  const { filterEntity } = meCtx.state;

  const byPass = watch('byPass');
  const byGroupTags = watch('byGroupTags');
  const byReader = watch('byReader');
  const byScanType = watch('byScanType');
  const byValidationType = watch('byValidationType');
  const byStatus = watch('byStatus');
  const byPassTemplate = watch('byPassTemplate');
  const byDateRange = watch('byDateRange');
  const createdAfter = watch('createdAfter');
  const createdBefore = watch('createdBefore');

  const handleCreatedAfter = useCallback(
    (date) => {
      setValue('createdAfter', date ? startOfDayISO(date) : '');
    },
    [setValue]
  );

  const handleCreatedBefore = useCallback(
    (date) => {
      setValue('createdBefore', date ? endOfDayISO(date) : '');
    },
    [setValue]
  );

  // FETCHING READERS
  useEffect(() => {
    const getReaders = async () => {
      try {
        const endpoint = filterEntity
          ? `api/v1/entities/${filterEntity.uuid}/readers`
          : 'api/v1/readers';
        const response = await authAxios.get(`${endpoint}?per_page=100`);
        const fetchedReaders = response?.data?.data?.map((reader) => {
          return { label: reader.attributes.name, value: reader.id };
        });
        setReaders(fetchedReaders);
      } catch (error) {
        reportAndToastApiError(error);
      }
    };

    getReaders();

    return () => {
      setReaders([]);
    };
  }, [authAxios, filterEntity]);

  // FETCHING PASS TEMPLATES
  useEffect(() => {
    const getPassTemplates = async () => {
      try {
        const endpoint = filterEntity
          ? `entities/${filterEntity.uuid}/passes/templates?skipImages=true&fields=name`
          : 'passes/templates?skipImages=true&fields=name';
        const response = await authAxios.get(endpoint);
        const fetchedPassTemplates = response?.data?.data?.map((template) => {
          return { label: template.name, value: template.uuid };
        });
        setPassTemplatesList(fetchedPassTemplates);
      } catch (error) {
        reportAndToastApiError(error);
      }
    };

    getPassTemplates();

    return () => {
      setPassTemplatesList([]);
    };
  }, [authAxios, filterEntity]);

  // fetch all group tags for select group tags dropdown
  useEffect(() => {
    const getGroupTags = async () => {
      try {
        const groupTagsList = await fetchGroupTags(
          authAxios,
          filterEntity,
          null
        );
        setGroupTagsList(groupTagsList);
      } catch (error) {
        sentry.sendError(error);
        console.log(error);
      }
    };

    getGroupTags();
  }, [authAxios, filterEntity]);

  useEffect(() => {
    onResetAll();
  }, [filterEntity]);

  const onSubmit = async (values) => {
    // on submit alter values object and add status from useState
    const filteredValues = {
      reader: values.byReader && values.reader ? values.reader : '',
      groupTags: values.byGroupTags && values.groupTags ? values.groupTags : [],
      pass: values.byPass && values.pass ? values.pass : '',
      scanType: values.byScanType && values.scanType ? values.scanType : '',
      status: values.byStatus && values.status ? values.status : '',
      validationType:
        values.byValidationType && values.validationType
          ? values.validationType
          : '',
      passTemplate:
        values.byPassTemplate && values.passTemplate ? values.passTemplate : [],
      createdAfter:
        values.byDateRange && values.createdAfter ? values.createdAfter : '',
      createdBefore:
        values.byDateRange && values.createdBefore ? values.createdBefore : '',
    };

    liveEventDataListCtx.updateFilterValues(filteredValues);

    let countableFilterValues = [];
    Object.entries(values).forEach(([key, value]) => {
      if (!key.startsWith('by') && value !== false) {
        countableFilterValues = countableFilterValues.concat(value);
      }
    });
    setTotalActiveFilters(
      countableFilterValues.filter((val) => val.length !== 0).length
    );
    onClose();
  };

  const onResetAll = async () => {
    reset({
      byReader: false,
      byGroupTags: false,
      byPass: false,
      byScanType: false,
      byStatus: false,
      byValidationType: false,
      byPassTemplate: false,
      byDateRange: false,
      ...emptyFilterValues(),
    });
    setDateRangeKey((key) => key + 1);

    liveEventDataListCtx.updateFilterValues(emptyFilterValues());
    setTotalActiveFilters(0);
    onClose();
  };

  return (
    <Flex
      alignSelf="flex-end"
      justify="flex-start"
      flexDirection={{ base: 'row-reverse', sm: 'row' }}
      flexGrow={{ base: '1', sm: 'unset' }}
    >
      {totalActiveFilters > 0 && <ClearFiltersButton onResetAll={onResetAll} />}
      <FilterButton onOpen={onOpen} totalActiveFilters={totalActiveFilters} />
      <Drawer onClose={onClose} isOpen={isOpen}>
        <DrawerOverlay />
        <form onSubmit={handleSubmit(onSubmit)}>
          <DrawerContent bg="white">
            <DrawerCloseButton />
            <DrawerHeader
              borderBottomWidth="1px"
              borderBottomColor="primaryBackground"
              pt={{ base: '16px', lg: '56px' }}
              bg="white"
            >
              Filter Scans
            </DrawerHeader>
            <DrawerBody p="0" bg="white">
              <VStack spacing={0}>
                <FormControl px="24px" mt="12px">
                  <Checkbox
                    id="byReader"
                    {...register('byReader', {
                      onChange: (e) => {
                        if (!e.target.checked) {
                          setValue('reader', '');
                        }
                      },
                    })}
                  >
                    <Text color="secondaryDark">By reader</Text>
                  </Checkbox>
                  <Collapse in={byReader} animateOpacity>
                    <Controller
                      name="reader"
                      control={control}
                      render={({ field }) => (
                        <CustomReactSelect
                          options={readers}
                          formatOptionLabel={formatOptionLabelSelectPassType}
                          placeholder="Select reader"
                          {...field}
                          id="passTypeFilter"
                        />
                      )}
                    />
                  </Collapse>
                </FormControl>
                <Box py="12px" w="full">
                  <Divider borderColor="primaryBackground" />
                </Box>

                <FormControl px="24px" mt="14px">
                  <Checkbox
                    id="byPass"
                    {...register('byPass', {
                      onChange: (e) => {
                        if (!e.target.checked) {
                          setValue('pass', '');
                        }
                      },
                    })}
                  >
                    <Text color="secondaryDark">By pass</Text>
                  </Checkbox>
                  <Collapse in={byPass} animateOpacity>
                    <Input
                      mt={2}
                      id="extId"
                      type="text"
                      placeholder="Enter pass UUID or External ID"
                      {...register('pass')}
                    />
                  </Collapse>
                </FormControl>
                <Box py="12px" w="full">
                  <Divider borderColor="primaryBackground" />
                </Box>

                <FormControl px="24px" mt="12px">
                  <Checkbox
                    id="byPassTemplate"
                    {...register('byPassTemplate', {
                      onChange: (e) => {
                        if (!e.target.checked) {
                          setValue('passTemplate', []);
                        }
                      },
                    })}
                  >
                    <Text color="secondaryDark">By pass template</Text>
                  </Checkbox>
                  <Collapse in={byPassTemplate} animateOpacity mt={2}>
                    <Controller
                      name="passTemplate"
                      control={control}
                      render={({ field }) => (
                        <CustomReactSelect
                          options={passTemplatesList}
                          formatOptionLabel={
                            formatOptionLabelFilterPassTemplate
                          }
                          placeholder="Select pass template"
                          noOptionsMessage={() => 'No pass templates found'}
                          menuPlacement="auto"
                          isMulti
                          passTemplate
                          {...field}
                          id="passTemplateFilter"
                        />
                      )}
                    />
                  </Collapse>
                </FormControl>
                <Box py="12px" w="full">
                  <Divider borderColor="primaryBackground" />
                </Box>

                <FormControl px="24px" mt="12px">
                  <Checkbox
                    id="byStatus"
                    {...register('byStatus', {
                      onChange: (e) => {
                        if (!e.target.checked) {
                          setValue('status', '');
                        }
                      },
                    })}
                  >
                    <Text color="secondaryDark">By status</Text>
                  </Checkbox>
                  <Collapse in={byStatus} animateOpacity mt={2}>
                    <Controller
                      name="status"
                      control={control}
                      render={({ field }) => (
                        <CustomReactSelect
                          options={[
                            {
                              label: 'Successful',
                              value: 'successful',
                            },
                            {
                              label: 'Failed',
                              value: 'failed',
                            },
                          ]}
                          formatOptionLabel={formatOptionLabelSelectPassType}
                          placeholder="Select status"
                          {...field}
                          id="groupTagFilter"
                          groupTags
                        />
                      )}
                    />
                  </Collapse>
                </FormControl>
                <Box py="12px" w="full">
                  <Divider borderColor="primaryBackground" />
                </Box>

                <FormControl px="24px" mt="12px">
                  <Checkbox
                    id="byGroupTags"
                    {...register('byGroupTags', {
                      onChange: (e) => {
                        if (!e.target.checked) {
                          setValue('groupTags', '');
                        }
                      },
                    })}
                  >
                    <Text color="secondaryDark">By group tag</Text>
                  </Checkbox>
                  <Collapse in={byGroupTags} animateOpacity>
                    <Controller
                      name="groupTags"
                      control={control}
                      render={({ field }) => (
                        <CustomSearchableSelect
                          options={groupTagsList}
                          placeholder="Select group tag"
                          formatOptionLabel={formatOptionLabelFilterGroupTag}
                          onChange={(groupTags) => {
                            field.onChange(groupTags);
                          }}
                          loadOptions={async (searchTerm) => {
                            try {
                              const groupTagsList = await fetchGroupTags(
                                authAxios,
                                filterEntity,
                                searchTerm
                              );
                              return groupTagsList;
                            } catch (error) {
                              console.error('Error loading group tags:', error);
                              return [];
                            }
                          }}
                          {...field}
                          value={field.value}
                          isMulti
                        />
                      )}
                    />
                  </Collapse>
                </FormControl>
                <Box py="12px" w="full">
                  <Divider borderColor="primaryBackground" />
                </Box>

                <FormControl px="24px" mt="12px">
                  <Checkbox
                    id="byScanType"
                    {...register('byScanType', {
                      onChange: (e) => {
                        if (!e.target.checked) {
                          setValue('scanType', '');
                        }
                      },
                    })}
                  >
                    <Text color="secondaryDark">By scan type</Text>
                  </Checkbox>
                  <Collapse in={byScanType} animateOpacity mt={2}>
                    <Controller
                      name="scanType"
                      control={control}
                      render={({ field }) => (
                        <CustomReactSelect
                          options={[
                            { label: 'Barcode', value: 'barcode' },
                            { label: 'NFC', value: 'nfc' },
                          ]}
                          formatOptionLabel={formatOptionLabelSelectPassType}
                          placeholder="Select scan type"
                          {...field}
                          id="groupTagFilter"
                          groupTag
                        />
                      )}
                    />
                  </Collapse>
                </FormControl>
                <Box py="12px" w="full">
                  <Divider borderColor="primaryBackground" />
                </Box>

                <FormControl px="24px" mt="12px">
                  <Checkbox
                    id="byValidationType"
                    {...register('byValidationType', {
                      onChange: (e) => {
                        if (!e.target.checked) {
                          setValue('validationType', '');
                        }
                      },
                    })}
                  >
                    <Text color="secondaryDark">By validation type</Text>
                  </Checkbox>
                  <Collapse in={byValidationType} animateOpacity mt={2}>
                    <Controller
                      name="validationType"
                      control={control}
                      render={({ field }) => (
                        <CustomReactSelect
                          options={[
                            {
                              label: 'PassEntry',
                              value: 'internal_validation',
                            },
                            {
                              label: 'Third Party',
                              value: 'external_validation',
                            },
                          ]}
                          formatOptionLabel={formatOptionLabelSelectPassType}
                          placeholder="Select scan type"
                          {...field}
                          id="groupTagFilter"
                          groupTag
                        />
                      )}
                    />
                  </Collapse>
                </FormControl>
                <Box py="12px" w="full">
                  <Divider borderColor="primaryBackground" />
                </Box>

                <FormControl px="24px" mt="12px">
                  <Checkbox
                    id="byDateRange"
                    {...register('byDateRange', {
                      onChange: (e) => {
                        if (!e.target.checked) {
                          setValue('createdAfter', '');
                          setValue('createdBefore', '');
                          setDateRangeKey((key) => key + 1);
                        }
                      },
                    })}
                  >
                    <Text color="secondaryDark">By date range</Text>
                  </Checkbox>
                  <Collapse in={byDateRange} animateOpacity mt={2}>
                    <Input type="hidden" {...register('createdAfter')} />
                    <Input type="hidden" {...register('createdBefore')} />
                    <Text fontSize="14px" color="secondaryDark08" mb="4px">
                      From
                    </Text>
                    <CustomDatePicker
                      key={`from-${dateRangeKey}`}
                      setDate={handleCreatedAfter}
                      maxBookingDate={
                        createdBefore ? new Date(createdBefore) : undefined
                      }
                      placementTop
                    />
                    <Text
                      fontSize="14px"
                      color="secondaryDark08"
                      mt="12px"
                      mb="4px"
                    >
                      To
                    </Text>
                    <CustomDatePicker
                      key={`to-${dateRangeKey}`}
                      setDate={handleCreatedBefore}
                      minBookingDate={
                        createdAfter ? new Date(createdAfter) : undefined
                      }
                      placementTop
                    />
                  </Collapse>
                </FormControl>
                <Box py="12px" w="full">
                  <Divider borderColor="primaryBackground" />
                </Box>
              </VStack>
            </DrawerBody>
            <DrawerFooter
              borderTopWidth="1px"
              borderTopColor="primaryBackground"
              bg="white"
            >
              <Link
                color={useColorModeValue('secondaryDark08', '#DDDDDD')}
                fontSize="14px"
                onClick={onResetAll}
                mr="30px"
              >
                <Text textStyle="headingFamilyMedium">Reset all</Text>
              </Link>
              <Button
                type="submit"
                alt="Apply filters"
                size="sm"
                px="30px"
                alignSelf="flex-start"
              >
                Apply filters
              </Button>
            </DrawerFooter>
          </DrawerContent>
        </form>
      </Drawer>
    </Flex>
  );
}

export default LiveEventDataListFilter;
